import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ShoppingBag, Plus, ChevronRight, Package, Search, ExternalLink, Sparkles, ChevronDown } from 'lucide-react';
import { API } from '@/App';

export default function ShopPage() {
  const navigate = useNavigate();
  const [brands, setBrands] = useState([]);
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [brandData, setBrandData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [featuredBrands, setFeaturedBrands] = useState([]);

  useEffect(() => {
    fetchBrands();
    fetchFeaturedBrands();
  }, []);

  const fetchBrands = async () => {
    try {
      const response = await fetch(`${API}/brands`);
      if (response.ok) {
        const data = await response.json();
        setBrands(data);
      }
    } catch (error) {
      console.error('Error fetching brands:', error);
    }
  };

  const fetchFeaturedBrands = async () => {
    try {
      const response = await fetch(`${API}/brand-checklists`);
      if (response.ok) {
        const data = await response.json();
        const uniqueBrands = [...new Set(data.map(bc => bc.brand))];
        setFeaturedBrands(uniqueBrands);
      }
    } catch (error) {
      console.error('Error fetching featured:', error);
    }
  };

  const selectBrand = useCallback(async (brandName, pg = 1) => {
    setLoading(true);
    setSelectedBrand(brandName);
    setPage(pg);
    try {
      const response = await fetch(`${API}/brand-profile/${encodeURIComponent(brandName)}?page=${pg}&limit=20`);
      if (response.ok) {
        const data = await response.json();
        if (pg === 1) {
          setBrandData(data);
        } else {
          setBrandData(prev => ({
            ...prev,
            products: [...prev.products, ...data.products],
            page: data.page,
            has_more: data.has_more
          }));
        }
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadMore = () => {
    if (selectedBrand) selectBrand(selectedBrand, page + 1);
  };

  const filteredBrands = searchQuery
    ? brands.filter(b => b.name.toLowerCase().includes(searchQuery.toLowerCase()))
    : brands;

  return (
    <div className="min-h-screen pb-24">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-background/80 backdrop-blur-xl border-b border-border">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center gap-3">
          <button data-testid="shop-back-btn" onClick={() => navigate(-1)} className="p-2 hover:bg-primary/10 rounded-full transition-colors">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-2">
            <ShoppingBag className="w-5 h-5 text-primary" />
            <h1 className="text-lg font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>Shop by Brand</h1>
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4">
        {/* Search */}
        <div className="py-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input
              data-testid="shop-search"
              type="text"
              placeholder="Search brands..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-3 rounded-xl border border-border focus:border-primary outline-none text-sm"
            />
          </div>
        </div>

        {/* Featured Brands */}
        {!searchQuery && featuredBrands.length > 0 && (
          <div className="mb-5">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="w-4 h-4 text-primary" />
              <h3 className="text-sm font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>Featured Brands</h3>
            </div>
            <div className="flex gap-2 overflow-x-auto scrollbar-hide pb-1">
              {featuredBrands.map(brand => (
                <button
                  key={brand}
                  data-testid={`featured-${brand}`}
                  onClick={() => selectBrand(brand)}
                  className={`shrink-0 px-4 py-2 rounded-full text-xs font-semibold transition-all ${
                    selectedBrand === brand
                      ? 'bg-primary text-white shadow-md'
                      : 'bg-primary/10 text-primary hover:bg-primary/20'
                  }`}
                >
                  {brand}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Brand Dropdown */}
        <div className="mb-4">
          <label className="block text-xs text-muted-foreground font-medium mb-1.5">All Brands ({filteredBrands.length})</label>
          <div className="relative">
            <select
              data-testid="brand-dropdown"
              value={selectedBrand || ''}
              onChange={(e) => e.target.value && selectBrand(e.target.value)}
              className="w-full px-4 py-3 rounded-xl border border-border focus:border-primary outline-none text-sm appearance-none bg-white pr-10"
            >
              <option value="">Select a brand...</option>
              {filteredBrands.map(b => (
                <option key={b.name} value={b.name}>{b.name} ({b.count} products)</option>
              ))}
            </select>
            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
          </div>
        </div>

        {/* Brand Content */}
        {selectedBrand && brandData && (
          <div className="border-t border-border pt-4">
            {/* Brand Header */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary/20 to-accent/20 flex items-center justify-center">
                  <span className="text-lg font-black text-primary" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    {brandData.brand.charAt(0)}
                  </span>
                </div>
                <div>
                  <h2 className="text-base font-bold" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="shop-brand-name">{brandData.brand}</h2>
                  <p className="text-xs text-muted-foreground flex items-center gap-1">
                    <Package className="w-3 h-3" />
                    {brandData.total_products} products
                  </p>
                </div>
              </div>
              <button
                data-testid="view-brand-profile-btn"
                onClick={() => navigate(`/brand/${encodeURIComponent(brandData.brand)}`)}
                className="text-xs text-primary font-semibold flex items-center gap-1 hover:underline"
              >
                Full Profile
                <ChevronRight className="w-3.5 h-3.5" />
              </button>
            </div>

            {/* Category Filter */}
            {brandData.categories?.length > 1 && (
              <div className="flex gap-2 flex-wrap mb-4">
                {brandData.categories.map(cat => (
                  <button
                    key={cat}
                    data-testid={`shop-filter-${cat}`}
                    onClick={() => {
                      setLoading(true);
                      setPage(1);
                      fetch(`${API}/brand-profile/${encodeURIComponent(brandData.brand)}?category=${cat}&limit=20`)
                        .then(r => r.json())
                        .then(data => { setBrandData(prev => ({ ...prev, products: data.products, filtered_count: data.filtered_count, has_more: data.has_more })); })
                        .finally(() => setLoading(false));
                    }}
                    className="px-3 py-1 rounded-full text-[11px] font-medium bg-gray-100 text-muted-foreground hover:bg-primary/10 hover:text-primary transition-colors"
                  >
                    {cat}
                  </button>
                ))}
              </div>
            )}

            {/* Products List */}
            {loading && !brandData.products?.length ? (
              <div className="py-8 text-center">
                <div className="w-8 h-8 border-3 border-primary border-t-transparent rounded-full animate-spin mx-auto" />
              </div>
            ) : (
              <div className="space-y-2">
                {brandData.products?.map(product => (
                  <div key={product.product_id} className="flex items-center gap-3 p-3 rounded-xl bg-white border border-border" data-testid={`shop-product-${product.product_id}`}>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground leading-tight">{product.name}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {product.product_type?.replace('_', ' ')}
                        {product.price ? ` · ${product.price}` : ''}
                      </p>
                    </div>
                    <div className="flex gap-1.5 shrink-0">
                      {product.purchase_url && (
                        <a
                          href={product.purchase_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1 px-2.5 py-1.5 rounded-full bg-primary text-white text-[10px] font-semibold hover:bg-primary/90 transition-colors"
                        >
                          <ShoppingBag className="w-3 h-3" />
                          Buy
                        </a>
                      )}
                      <button
                        onClick={() => navigate('/create-checklist', { state: { preselectedProduct: product.product_id } })}
                        className="flex items-center gap-1 px-2.5 py-1.5 rounded-full border border-primary text-primary text-[10px] font-semibold hover:bg-primary/5 transition-colors"
                      >
                        <Plus className="w-3 h-3" />
                        Add
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Load More */}
            {brandData.has_more && (
              <button
                data-testid="shop-load-more"
                onClick={loadMore}
                disabled={loading}
                className="w-full mt-4 py-3 rounded-xl border border-border text-sm font-medium text-muted-foreground hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                {loading ? 'Loading...' : 'Load more products'}
              </button>
            )}
          </div>
        )}

        {/* Empty state when no brand selected */}
        {!selectedBrand && (
          <div className="text-center py-12">
            <ShoppingBag className="w-12 h-12 text-muted-foreground/30 mx-auto mb-3" />
            <p className="text-sm text-muted-foreground">Select a brand to browse their products</p>
          </div>
        )}
      </div>
    </div>
  );
}
